/**
 * Circle rules (spec §9.29): rotation turns and the `mine` predicate — mirror of `circle.py`; both
 * pass `tests/vectors/rotation.json`.
 */
import { roundOf, rotates, type Fairness } from "./parts";

/** `subs`: the task's open subtasks in order, for a task with rotating subtasks (ADR 0035) */
type MineOpts = {
  shared: boolean;
  members?: string[] | null;
  subs?: string[] | null;
  /** the workspace's `fairnessOf` (ADR 0037) */
  fair?: Fairness | null;
};
type TaskRec = { id?: string; props?: Record<string, unknown>; doneAt?: string | null };

const props = (t: TaskRec) => (t.props && typeof t.props === "object" ? t.props : {});
const profileId = (by: string) => (by.startsWith("profile:") ? by.slice(8) : by);

export function rotationOf(task: TaskRec, members?: string[] | null): string[] {
  const rot = props(task)["rec.rotation"];
  const out = Array.isArray(rot) ? rot.map(String) : [];
  return members ? out.filter((x) => members.includes(x)) : out;
}

export function lastDoer(task: TaskRec): string | null {
  const hist = props(task)["rec.history"];
  if (!Array.isArray(hist)) return null;
  for (let i = hist.length - 1; i >= 0; i--) {
    const e = hist[i] as { by?: unknown; skipped?: boolean } | null;
    if (e && typeof e === "object" && !e.skipped && typeof e.by === "string") return profileId(e.by);
  }
  return null;
}

export function turn(task: TaskRec, members?: string[] | null): string | null {
  const full = rotationOf(task);
  const rot = rotationOf(task, members);
  if (!rot.length) return null;
  const last = lastDoer(task);
  if (last === null) return rot[0];
  if (rot.includes(last)) return rot[(rot.indexOf(last) + 1) % rot.length];
  if (full.includes(last)) {
    const i = full.indexOf(last);
    for (let step = 1; step <= full.length; step++) {
      const cand = full[(i + step) % full.length];
      if (rot.includes(cand)) return cand;
    }
  }
  return rot[0];
}

export function assigneesOf(task: TaskRec, members?: string[] | null): string[] {
  const a = props(task).assignee;
  const out = Array.isArray(a) ? a.map(String) : typeof a === "string" && a ? [a] : [];
  return members ? out.filter((x) => members.includes(x)) : out;
}

export function isAnyone(task: TaskRec, members?: string[] | null): boolean {
  return !assigneesOf(task, members).length && !rotationOf(task, members).length;
}

export function mine(task: TaskRec, profile: string, opts: MineOpts): boolean {
  if (!opts.shared) return true;
  // rotating subtasks with people taking turns: it is theirs while their part of the round is open (ADR 0035)
  if (opts.subs && rotates(task)) {
    const round = roundOf(task, opts.subs, { shared: true, members: opts.members, fair: opts.fair });
    if (round && round.parts.some((x) => x.who !== null))
      return round.parts.some((x) => !x.done && x.who === profile);
  }
  // taking turns decides when it applies: the assigned people share the task, but it is only theirs in turn
  const whose = turn(task, opts.members);
  if (whose !== null) return whose === profile;
  if (assigneesOf(task, opts.members).includes(profile)) return true;
  return isAnyone(task, opts.members);
}

/** The `for:` selector of bins, /today, /wants: `me` | `anyone` | `<profileId>`. */
export function matchesFor(task: TaskRec, who: string, me: string, opts: MineOpts): boolean {
  if (who === "anyone" || !who) return true;
  return mine(task, who === "me" ? me : who, opts);
}

/** [kept, total] over non-skipped completions of rotating tasks: done by whose turn it was? */
export function rotationAdherence(tasks: TaskRec[]): [number, number] {
  let kept = 0;
  let total = 0;
  for (const t of tasks) {
    // a done copy of a fixed-schedule repeat: its turns are counted in the open copy
    if (t.doneAt && props(t)["repeat.rrule"]) continue;
    const rot = rotationOf(t);
    const hist = props(t)["rec.history"];
    if (rot.length < 2 || !Array.isArray(hist)) continue;
    let expected = rot[0];
    for (const raw of hist) {
      const e = raw as { by?: unknown; skipped?: boolean } | null;
      if (!e || typeof e !== "object" || e.skipped || typeof e.by !== "string") continue;
      const who = profileId(e.by);
      total++;
      if (who === expected) kept++;
      if (rot.includes(who)) expected = rot[(rot.indexOf(who) + 1) % rot.length];
    }
  }
  return [kept, total];
}
